// S3 + manifest helpers. Uses Cognito-issued AWS credentials (the authenticated
// role has s3:PutObject + s3:DeleteObject on the decks bucket).
//
// The deck library is represented by /manifest.json at the bucket root —
// updated atomically on every upload/delete.
//
// File uploads use presigned PUT URLs + XHR (not the SDK's PutObjectCommand
// directly) so we get reliable progress events and avoid SDK quirks around
// Blob/File body streaming.

import { S3Client, PutObjectCommand, GetObjectCommand, DeleteObjectCommand, ListObjectsV2Command, HeadObjectCommand } from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';

let s3 = null;
let bucket = null;
let region = null;

export function initStorage({ region: r, decksBucket, creds }) {
  bucket = decksBucket;
  region = r;
  s3 = new S3Client({
    region: r,
    credentials: {
      accessKeyId:     creds.accessKeyId,
      secretAccessKey: creds.secretAccessKey,
      sessionToken:    creds.sessionToken,
    },
  });
}

async function getJson(key) {
  try {
    const out = await s3.send(new GetObjectCommand({ Bucket: bucket, Key: key }));
    const text = await out.Body.transformToString();
    return JSON.parse(text);
  } catch (e) {
    if (e.name === 'NoSuchKey') return null;
    throw e;
  }
}

async function putJson(key, obj) {
  await s3.send(new PutObjectCommand({
    Bucket: bucket,
    Key:    key,
    Body:   JSON.stringify(obj, null, 2),
    ContentType: 'application/json',
    CacheControl: 'no-cache',
  }));
}

export async function loadManifest() {
  return (await getJson('manifest.json')) ?? { decks: [] };
}

async function saveManifest(m) {
  await putJson('manifest.json', m);
}

/** Upload a deck HTML file under decks/<deckId>/index.html and update manifest. */
export async function uploadDeck({ deckId, title, htmlBlob, onProgress }) {
  const key = `decks/${deckId}/index.html`;
  const size = htmlBlob.size;

  // 1) Get a presigned PUT URL.
  const putCmd = new PutObjectCommand({
    Bucket: bucket,
    Key:    key,
    ContentType: 'text/html; charset=utf-8',
    CacheControl: 'public, max-age=3600',
  });
  const url = await getSignedUrl(s3, putCmd, { expiresIn: 900 });

  // 2) Upload via XHR — gives us upload-progress events and avoids the SDK's
  //    body-streaming code path (which tripped on File objects in the browser).
  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', 'text/html; charset=utf-8');
    xhr.setRequestHeader('Cache-Control', 'public, max-age=3600');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress({ phase: 'upload', loaded: e.loaded, total: e.total });
    };
    xhr.onload  = () => xhr.status >= 200 && xhr.status < 300
      ? resolve()
      : reject(new Error(`PUT ${xhr.status}: ${xhr.responseText}`));
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.send(htmlBlob);
  });
  if (onProgress) onProgress({ phase: 'upload', loaded: size, total: size });

  // Save metadata.
  const meta = { id: deckId, title: title || deckId, uploadedAt: Date.now(), size };
  await putJson(`decks/${deckId}/meta.json`, meta);

  // Update manifest.
  const m = await loadManifest();
  m.decks = (m.decks || []).filter(d => d.id !== deckId);
  m.decks.push(meta);
  m.decks.sort((a, b) => b.uploadedAt - a.uploadedAt);
  await saveManifest(m);

  return meta;
}

/**
 * Allocate a 3-digit session ID (100–999) and write its manifest so the
 * viewer page at /<id> can resolve which deck to load.
 * Retries on collision (HEAD existing manifest).
 */
export async function createSession(deckId) {
  for (let attempt = 0; attempt < 25; attempt++) {
    const id = String(100 + Math.floor(Math.random() * 900));
    const Key = `sessions/${id}.json`;
    let exists = false;
    try {
      await s3.send(new HeadObjectCommand({ Bucket: bucket, Key }));
      exists = true;
    } catch (e) {
      // 404 → free; anything else is a real error
      if (e.name !== 'NotFound' && e.$metadata?.httpStatusCode !== 404) throw e;
    }
    if (exists) continue;
    await s3.send(new PutObjectCommand({
      Bucket: bucket,
      Key,
      Body: JSON.stringify({ deckId, createdAt: Date.now() }),
      ContentType: 'application/json',
      CacheControl: 'no-cache',
    }));
    return id;
  }
  throw new Error('Could not allocate a session ID — try again');
}

/** Delete the session manifest so /<id> stops resolving. */
export async function endSession(sessionId) {
  await s3.send(new DeleteObjectCommand({
    Bucket: bucket,
    Key: `sessions/${sessionId}.json`,
  }));
}

export async function deleteDeck(deckId) {
  // List and delete every object under decks/<deckId>/
  const list = await s3.send(new ListObjectsV2Command({
    Bucket: bucket,
    Prefix: `decks/${deckId}/`,
  }));
  for (const obj of list.Contents || []) {
    await s3.send(new DeleteObjectCommand({ Bucket: bucket, Key: obj.Key }));
  }
  const m = await loadManifest();
  m.decks = (m.decks || []).filter(d => d.id !== deckId);
  await saveManifest(m);
}
